<template>
  <div>
    <v-row>
      <v-col>
        <s-crud
          title="Alias de Objetos"
          :config="config"
          edit
          add
          search-input
          remove
          ref="crud"
          height="200"
          @save="save($event)"
          @rowSelected="rowSelected($event)"
        >
          <template slot-scope="props">
            <div class="config-form-wrapper">
              <!-- Selector de Tipo de Objeto - Destacado -->
              <div class="type-selector-section">
                <div class="section-label">
                  <v-icon small color="primary" class="mr-2">mdi-shape-outline</v-icon>
                  <span>Tipo de Objeto</span>
                </div>
                <div class="type-selector-card">
                  <s-select-definition
                    :def="1364"
                    label="Selecciona el tipo"
                    v-model="props.item.TypeObject"
                    :searchable="true"
                    @change="onChangeTypeObject(props.item)"
                  />
                  <transition name="fade">
                    <div v-if="props.item.TypeObject !== null && props.item.TypeObject !== undefined" class="type-hint">
                      <span v-if="props.item.TypeObject === 0" class="hint-text">
                        <v-icon x-small class="mr-1">mdi-information</v-icon>
                        Configurarás Tabla + Columna + Definición
                      </span>
                      <span v-else-if="props.item.TypeObject === 1" class="hint-text">
                        <v-icon x-small class="mr-1">mdi-information</v-icon>
                        Configurarás solo Tabla
                      </span>
                      <span v-else-if="props.item.TypeObject === 2" class="hint-text">
                        <v-icon x-small class="mr-1">mdi-information</v-icon>
                        Configurarás solo Columna
                      </span>
                    </div>
                  </transition>
                </div>
              </div>

              <!-- Configuración Principal -->
              <div class="config-grid">
                <!-- Tabla (TypeObject: 0, 1 y 2) -->
                <transition name="slide-fade">
                  <div 
                    v-if="[0, 1, 2].includes(props.item.TypeObject)" 
                    class="config-card"
                    :class="{ 'card-required': props.item.TypeObject === 0 || props.item.TypeObject === 2 }"
                  >
                    <div class="card-icon">
                      <v-icon color="primary">mdi-table</v-icon>
                    </div>
                    <div class="card-content">
                      <label class="field-label">Tabla</label>
                      <s-select
                        :items="tablesItems"
                        item-value="value"
                        item-text="text"
                        placeholder="Selecciona una tabla"
                        v-model="props.item.AlsTableName"
                        @change="onChangeTable($event, props.item)"
                        autocomplete
                        clearable
                      />
                    </div>
                  </div>
                </transition>

                <!-- Columna (TypeObject: 0 y 2) -->
                <transition name="slide-fade">
                  <div 
                    v-if="[0, 2].includes(props.item.TypeObject)" 
                    class="config-card"
                    :class="{ 
                      'card-required': props.item.TypeObject === 0 || props.item.TypeObject === 2,
                      'card-disabled': !props.item.AlsTableName
                    }"
                  >
                    <div class="card-icon">
                      <v-icon :color="!props.item.AlsTableName ? 'grey' : 'primary'">
                        mdi-table-column
                      </v-icon>
                    </div>
                    <div class="card-content">
                      <label class="field-label">
                        Columna
                        <span v-if="!props.item.AlsTableName" class="required-hint">(requiere tabla)</span>
                      </label>
                      <s-select
                        :items="columnsItems"
                        item-value="value"
                        item-text="text"
                        placeholder="Selecciona una columna"
                        v-model="props.item.AlsNameObj"
                        autocomplete
                        clearable
                        :disabled="!props.item.AlsTableName"
                      />
                    </div>
                  </div>
                </transition>

                <!-- Definición (TypeObject: 0) -->
                <transition name="slide-fade">
                  <div v-if="props.item.TypeObject === 0" class="config-card">
                    <div class="card-icon">
                      <v-icon color="primary">mdi-file-document-outline</v-icon>
                    </div>
                    <div class="card-content">
                      <label class="field-label">Definición</label>
                      <s-text
                        placeholder="ID de definición"
                        v-model="props.item.DefID"
                      />
                    </div>
                  </div>
                </transition>

                <!-- Alias (Siempre visible) -->
                <transition name="slide-fade">
                  <div 
                    v-if="props.item.TypeObject !== null && props.item.TypeObject !== undefined"
                    class="config-card card-highlight"
                  >
                    <div class="card-icon">
                      <v-icon color="success">mdi-tag-outline</v-icon>
                    </div>
                    <div class="card-content">
                      <label class="field-label">
                        Alias 
                        <span class="optional-badge">Recomendado</span>
                      </label>
                      <s-text
                        placeholder="Nombre personalizado"
                        v-model="props.item.AlsAlias"
                      />
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Resumen de Configuración -->
              <transition name="fade">
                <div v-if="hasConfiguration(props.item)" class="summary-card">
                  <div class="summary-header">
                    <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                    <span>Configuración Actual</span>
                  </div>
                  <div class="summary-content">
                    <div v-if="props.item.AlsTableName" class="summary-item">
                      <v-icon x-small class="mr-2">mdi-table</v-icon>
                      <strong>Tabla:</strong>
                      <span class="ml-1">{{ getTableText(props.item.AlsTableName) }}</span>
                    </div>
                    <div v-if="props.item.AlsNameObj" class="summary-item">
                      <v-icon x-small class="mr-2">mdi-table-column</v-icon>
                      <strong>Columna:</strong>
                      <span class="ml-1">{{ getColumnText(props.item.AlsNameObj) }}</span>
                    </div>
                    <div v-if="props.item.DefID" class="summary-item">
                      <v-icon x-small class="mr-2">mdi-file-document-outline</v-icon>
                      <strong>Definición:</strong>
                      <span class="ml-1">{{ props.item.DefID }}</span>
                    </div>
                    <div v-if="props.item.AlsAlias" class="summary-item">
                      <v-icon x-small class="mr-2">mdi-tag</v-icon>
                      <strong>Alias:</strong>
                      <span class="ml-1 alias-text">{{ props.item.AlsAlias }}</span>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </template>
        </s-crud>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _sService from "@/services/General/Alias/AliasService.js";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";

export default {
  name: "AliasG",
  components: { SSelectDefinition },
  data() {
    return {
      row: {},
      tablesItems: [],
      columnsItems: [],
    };
  },
  computed: {
    config() {
      return {
        model: {
          AlsID: "ID",
          TypeObject: "number",
          AlsTableName: "string",
          AlsNameObj: "string",
          AlsAlias: "string",
          DefID: "string",
        },
        service: _sService,
        headers: [
          { text: "ID", value: "AlsID", sortable: false },
          { text: "Tipo", value: "TypeObjectName", sortable: false },
          { text: "Tabla", value: "AlsTableName", sortable: false },
          { text: "Objeto", value: "AlsNameObj", sortable: false },
          { text: "Alias", value: "AlsAlias", sortable: false },
        ],
      };
    },
  },
  methods: {
    hasConfiguration(item) {
      return item.AlsTableName || item.AlsNameObj || item.DefID || item.AlsAlias;
    },
    getTableText(value) {
      if (!value) return "";
      const table = this.tablesItems.find(t => t.value === value);
      return table ? table.text : value;
    },
    getColumnText(value) {
      if (!value) return "";
      const column = this.columnsItems.find(c => c.value === value);
      return column ? column.text : value;
    },
    rowSelected(rows) {
      this.row = rows && rows.length ? rows[0] : {};
      if (this.row && (this.row.AlsTableName || this.row.AlsNameObj)) {
        this.loadColumns(this.row.AlsTableName || this.row.AlsNameObj);
      }
    },
    async save(item) {
      if (item.TypeObject === null || item.TypeObject === undefined)
        return this.$fun.alert("Seleccione un tipo de objeto", "warning");

      if (!item.AlsAlias) 
        return this.$fun.alert("Ingrese un alias", "warning");

      if (item.TypeObject === 0) {
        if (!item.AlsTableName) 
          return this.$fun.alert("Seleccione una tabla", "warning");
        if (!item.AlsNameObj) 
          return this.$fun.alert("Seleccione una columna", "warning");
      }
      
      if (item.TypeObject === 1) {
        if (!item.AlsTableName) 
          return this.$fun.alert("Seleccione una tabla", "warning");
        item.AlsNameObj = item.AlsTableName; // El objeto es la misma tabla
      }
      
      if (item.TypeObject === 2) {
        if (!item.AlsTableName)
          return this.$fun.alert("Seleccione una tabla para listar columnas", "warning");
        if (!item.AlsNameObj) 
          return this.$fun.alert("Seleccione una columna", "warning");
      }

      // Normalizar estados a 0/1
      item.AlsStatus = item.AlsStatus ? 1 : 0;
      item.SecStatus = item.SecStatus ? 1 : 0;

      // Validar duplicados contra el backend
      const dup = await this.validateDuplicates(item);
      if (dup) return this.$fun.alert(dup, 'warning');

      item.save().then(() => {
        this.$refs.crud.refresh();
        this.rowSelected([item]);
      });
    },
    onChangeTable(value, item) {      
      const table = value && value.value ? value.value : value;
      item.AlsTableName = table;

      if (item.TypeObject === 1) {
        item.AlsNameObj = table;
      } else {
        item.AlsNameObj = null;
      }

      this.columnsItems = [];
      if (table) {
        this.loadColumns(table);
      }
    },
    onChangeTypeObject(item) {
      // Limpiar campos al cambiar de tipo para evitar inconsistencias
      if (item.TypeObject === 0 && item.AlsID == 0) {
        item.AlsTableName = null;
        item.AlsNameObj = null;
      } else if (item.TypeObject === 1) {
        item.AlsNameObj = item.AlsTableName;
      } else if (item.TypeObject === 2) {
        // Para columnas, requerimos una tabla para cargar columnas
        if (!item.AlsTableName) {
          item.AlsNameObj = null;
        }
      }
    },
    async loadTables() {
      const r = await _sService.tables("", this.$fun.getUserID());
      if (r.status === 200) {
        this.tablesItems = (r.data || []).map((x) => ({
          value: x.AlsNameObj,
          text: x.AlsNameObj,
        }));
      }
    },
    async loadColumns(table) {
      const r = await _sService.columns(table, "", this.$fun.getUserID());
      if (r.status === 200) {
        this.columnsItems = (r.data || []).map((x) => ({
          value: x.AlsNameObj,
          text: x.AlsNameObj,
        }));
      }
    },

    async validateDuplicates(item) {
      try {
        const req = { draw: 0, start: 0, length: 0, orderColumn: 0, orderDir: 'asc', searchText: '' };
        const r = await this.$http.post('/api/genobjectalias/pagination', req, { params: { requestID: this.$fun.getUserID() } });
        if (r.status !== 200) return null;
        const rows = (r.data && r.data.data) ? r.data.data : [];
        const norm = s => (s || '').toString().trim().toUpperCase().replace(/\s+/g, ' ');
        const isSame = x => item.AlsID && Number(x.AlsID) === Number(item.AlsID);

        // Alias único por TypeObject
        const existAlias = rows.some(x => !isSame(x) && Number(x.TypeObject) === Number(item.TypeObject) && norm(x.AlsAlias) === norm(item.AlsAlias));
        if (existAlias) return 'Ya existe un alias con el mismo nombre dentro del mismo TypeObject.';

        // Unicidad por objeto físico/definición, incluyendo estados
        if (Number(item.TypeObject) === 1) {
          const exist = rows.some(x => !isSame(x) && Number(x.TypeObject) === 1 && norm(x.AlsNameObj) === norm(item.AlsNameObj) && Number(x.AlsStatus) === Number(item.AlsStatus) && Number(x.SecStatus) === Number(item.SecStatus));
          if (exist) return 'Ya existe un alias para esta tabla con los mismos estados.';
        }
        if (Number(item.TypeObject) === 2) {
          const exist = rows.some(x => !isSame(x) && Number(x.TypeObject) === 2 && norm(x.AlsTableName) === norm(item.AlsTableName) && norm(x.AlsNameObj) === norm(item.AlsNameObj) && Number(x.AlsStatus) === Number(item.AlsStatus) && Number(x.SecStatus) === Number(item.SecStatus));
          if (exist) return 'Ya existe un alias para esta columna con los mismos estados.';
        }
        if (Number(item.TypeObject) === 0) {
          const exist = rows.some(x => !isSame(x) && Number(x.TypeObject) === 0 && Number(x.DefID) === Number(item.DefID) && Number(x.AlsStatus) === Number(item.AlsStatus) && Number(x.SecStatus) === Number(item.SecStatus));
          if (exist) return 'Ya existe un alias para esta definición con los mismos estados.';
        }
        return null;
      } catch {
        return null;
      }
    },
  },
  created() {
    this.loadTables();
  },
};
</script>

<style scoped>
/* === CONTENEDOR PRINCIPAL === */
.config-form-wrapper {
  padding: 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

/* === SELECTOR DE TIPO === */
.type-selector-section {
  margin-bottom: 28px;
}

.section-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.type-selector-card {
  background: #fff;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.type-hint {
  margin-top: 12px;
  padding: 8px 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.hint-text {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #0d47a1;
  font-weight: 500;
}

/* === GRID DE CONFIGURACIÓN === */
.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

/* === CARDS DE CONFIGURACIÓN === */
.config-card {
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  display: flex;
  gap: 16px;
}

.card-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.card-disabled:hover {
  transform: none;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f9fafb;
  border-radius: 8px;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
  min-width: 0;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.required-hint {
  font-size: 10px;
  color: #ff9800;
  font-weight: 500;
  font-style: italic;
  text-transform: none;
}

.optional-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #eef2ff;
  color: #3730a3;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 6px;
}

/* === RESUMEN === */
.summary-card {
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
}

.summary-header {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #2e7d32;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.summary-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border-radius: 8px;
  font-size: 13px;
  color: #2d3748;
  border: 1px solid #e5e7eb;
}

.summary-item strong {
  color: #4a5568;
  margin-right: 4px;
}

.alias-text {
  color: #4caf50;
  font-weight: 600;
}

/* === ANIMACIONES === */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter {
  transform: translateY(-6px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-6px);
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.4s ease;
}

.fade-enter {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* === RESPONSIVE === */
@media (max-width: 960px) {
  .config-form-wrapper {
    padding: 16px;
  }

  .config-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .summary-content {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .config-card {
    padding: 14px;
  }

  .card-icon {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 600px) {
  .type-selector-card {
    padding: 14px;
  }

  .config-card {
    flex-direction: column;
    gap: 12px;
  }

  .card-icon {
    width: 100%;
    height: 48px;
  }

  .summary-card {
    padding: 16px;
  }

  .section-label {
    font-size: 12px;
  }

  .field-label {
    font-size: 11px;
  }
}
</style>